<template>
	<v-card class="mx-auto" max-width="600">
		<v-card-title class="headline">
			Set Up Multi-Factor Authentication
		</v-card-title>
		
		<v-card-subtitle v-if="mfaEnforcementDate">
			<v-alert type="warning" dense>
				Your organization requires MFA. You have until {{ formattedEnforcementDate }} to complete setup.
				<br><strong>Days remaining: {{ daysRemaining }}</strong>
			</v-alert>
		</v-card-subtitle>
		
		<v-card-text>
			<v-stepper v-model="step" vertical>
				<!-- Step 1: Choose MFA Method -->
				<v-stepper-step :complete="step > 1" step="1">
					Choose Authentication Method
				</v-stepper-step>
				<v-stepper-content step="1">
					<v-radio-group v-model="mfaMethod">
						<v-radio label="Authenticator App (Recommended)" value="TOTP">
							<template v-slot:label>
								<div>
									<strong>Authenticator App (Recommended)</strong>
									<p class="text-caption mb-0">Use Google Authenticator, Authy, or similar apps</p>
								</div>
							</template>
						</v-radio>
						<v-radio label="SMS Text Message" value="SMS">
							<template v-slot:label>
								<div>
									<strong>SMS Text Message</strong>
									<p class="text-caption mb-0">Receive codes via text message</p>
								</div>
							</template>
						</v-radio>
					</v-radio-group>
					<v-btn color="primary" @click="proceedToSetup" :disabled="!mfaMethod">Continue</v-btn>
					<v-btn text @click="skipSetup" v-if="canSkip">Skip for Now</v-btn>
				</v-stepper-content>
				
				<!-- Step 2: TOTP Setup -->
				<v-stepper-step :complete="step > 2" step="2" v-if="mfaMethod === 'TOTP'">
					Scan QR Code
				</v-stepper-step>
				<v-stepper-content step="2" v-if="mfaMethod === 'TOTP'">
					<v-progress-circular v-if="loadingQr" indeterminate color="primary"></v-progress-circular>
					<div v-else-if="qrCodeDataUrl">
						<p>Scan this QR code with your authenticator app:</p>
						<img :src="qrCodeDataUrl" alt="QR Code" class="qr-code"/>
						<v-expansion-panels class="mt-4">
							<v-expansion-panel>
								<v-expansion-panel-header>Can't scan? Enter manually</v-expansion-panel-header>
								<v-expansion-panel-content>
									<p>Secret Key:</p>
									<v-text-field 
										:value="secretCode" 
										readonly 
										outlined 
										dense
										append-icon="mdi-content-copy"
										@click:append="copyToClipboard(secretCode)"
									></v-text-field>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
						<v-btn color="primary" @click="step = 3" class="mt-4">Continue</v-btn>
						<v-btn text @click="step = 1">Back</v-btn>
					</div>
					<v-alert v-else type="error">Failed to generate QR code. Please try again.</v-alert>
				</v-stepper-content>
				
				<!-- Step 2: SMS Setup -->
				<v-stepper-step :complete="step > 2" step="2" v-if="mfaMethod === 'SMS'">
					Enter Phone Number
				</v-stepper-step>
				<v-stepper-content step="2" v-if="mfaMethod === 'SMS'">
					<v-text-field
						v-model="phoneNumberLocal"
						label="Phone Number"
						hint="Enter your 10-digit phone number (country code +1 will be added automatically)"
						persistent-hint
						outlined
						:rules="[rules.required, rules.phoneLocal]"
						@input="formatPhoneNumber"
					></v-text-field>
					<v-alert v-if="verificationError" type="error" dismissible @input="verificationError = null">{{ verificationError }}</v-alert>
					<v-btn color="primary" @click="setupSms" :disabled="!isValidPhone" :loading="sendingSms">Continue</v-btn>
					<v-btn text @click="step = 1">Back</v-btn>
				</v-stepper-content>
				
				<!-- Step 3: Verify Code -->
				<v-stepper-step :complete="step > 3" step="3">
					Verify Code
				</v-stepper-step>
				<v-stepper-content step="3">
					<p v-if="mfaMethod === 'TOTP'">Enter the 6-digit code from your authenticator app:</p>
					<p v-else>Enter the verification code sent to your phone:</p>
					<v-text-field
						v-model="verificationCode"
						label="Verification Code"
						outlined
						:rules="[rules.required, rules.sixDigits]"
						maxlength="6"
					></v-text-field>
					<v-alert v-if="verificationError" type="error" dismissible>{{ verificationError }}</v-alert>
					<v-btn color="primary" @click="verifyCode" :loading="verifying" :disabled="!verificationCode || verificationCode.length !== 6">Verify</v-btn>
					<v-btn text @click="step = 2">Back</v-btn>
				</v-stepper-content>
				
				<!-- Step 4: Success -->
				<v-stepper-step step="4" v-if="setupComplete">
					Setup Complete!
				</v-stepper-step>
				<v-stepper-content step="4" v-if="setupComplete">
					<v-alert type="success">
						MFA has been successfully enabled for your account.
						You'll be prompted for a code on your next login.
					</v-alert>
					<v-btn color="primary" @click="closeDialog">Done</v-btn>
				</v-stepper-content>
			</v-stepper>
		</v-card-text>
	</v-card>
</template>

<script>
import orgService from "../service/org-service";
import moment from 'moment';

export default {
	name: "MfaSetup",
	props: {
		user: {
			type: Object,
			required: false,
			default: null
		}
	},
	data() {
		return {
			step: 1,
			mfaMethod: null,
			countryCode: '1', // Default to USA
			phoneNumberLocal: '',
			qrCodeDataUrl: null,
			secretCode: null,
			session: null,
			verificationCode: '',
			verificationError: null,
			loadingQr: false,
			verifying: false,
			sendingSms: false,
			setupComplete: false,
			rules: {
				required: value => value && value.length > 0 || 'Required',
				phone: value => {
					const pattern = /^\+[1-9]\d{1,14}$/;
					return pattern.test(value) || 'Invalid phone number format (e.g., +1234567890)';
				},
				phoneLocal: value => {
					const pattern = /^\d{10}$/;
					return pattern.test(value) || 'Must be a 10-digit phone number';
				},
				sixDigits: value => {
					const pattern = /^\d{6}$/;
					return pattern.test(value) || 'Must be 6 digits';
				}
			}
		};
	},
	computed: {
		mfaEnforcementDate() {
			return this.user && this.user.mfaEnforcementDate ? new Date(this.user.mfaEnforcementDate) : null;
		},
		formattedEnforcementDate() {
			return this.mfaEnforcementDate ? moment(this.mfaEnforcementDate).format('MMMM D, YYYY') : '';
		},
		daysRemaining() {
			if (!this.mfaEnforcementDate) return null;
			const now = new Date();
			const diff = this.mfaEnforcementDate - now;
			const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
			return days > 0 ? days : 0;
		},
		canSkip() {
			return this.daysRemaining && this.daysRemaining > 0;
		},
		phoneNumber() {
			// Return phone number without country code for storage
			// Country code (+1) will be added only when sending to Cognito SMS
			return this.phoneNumberLocal;
		},
		phoneNumberWithCountryCode() {
			// Build full phone number with country code for Cognito SMS
			if (this.phoneNumberLocal && this.phoneNumberLocal.length === 10) {
				return '+' + this.countryCode + this.phoneNumberLocal;
			}
			return '';
		},
		isValidPhone() {
			return this.phoneNumberLocal && this.rules.phoneLocal(this.phoneNumberLocal) === true;
		}
	},
	methods: {
		formatPhoneNumber() {
			// Remove any non-digit characters
			this.phoneNumberLocal = this.phoneNumberLocal.replace(/\D/g, '');
			// Limit to 10 digits
			if (this.phoneNumberLocal.length > 10) {
				this.phoneNumberLocal = this.phoneNumberLocal.substring(0, 10);
			}
		},
		async proceedToSetup() {
			this.step = 2;
			if (this.mfaMethod === 'TOTP') {
				await this.loadQrCode();
			}
		},
		async loadQrCode() {
			this.loadingQr = true;
			try {
				// Get current user's Cognito access token
				const Auth = await import('@aws-amplify/auth').then(m => m.Auth);
				const session = await Auth.currentSession();
				const accessToken = session.getAccessToken().getJwtToken();
				
				const result = await orgService.query("getMfaQrCode", { accessToken });
				if (result && result.success) {
					this.qrCodeDataUrl = result.qrCodeDataUrl;
					this.secretCode = result.secretCode;
					this.session = result.session;
				} else {
					this.verificationError = "Failed to generate QR code. Please try again.";
				}
			} catch (error) {
				console.error("Error loading QR code:", error);
				this.verificationError = "Failed to load MFA setup. Please try again.";
			} finally {
				this.loadingQr = false;
			}
		},
		async setupSms() {
			console.log("setupSms called - phone number:", this.phoneNumber);
			this.sendingSms = true;
			this.verificationError = null;
			try {
				// Get current user's Cognito access token
				const Auth = await import('@aws-amplify/auth').then(m => m.Auth);
				const session = await Auth.currentSession();
				const accessToken = session.getAccessToken().getJwtToken();
				
				// Call setupUserMfa to set phone number and send verification SMS
				console.log("Calling setupUserMfa mutation with phone:", this.phoneNumber);
				const result = await orgService.mutation("setupUserMfa", {
					mfaMethod: 'SMS',
					phoneNumber: this.phoneNumber,
					accessToken: accessToken
				});
				console.log("setupUserMfa result:", result);
				
				if (result && result.success) {
					// SMS sent successfully, move to verification step
					this.step = 3;
					// Show success message
					console.log("SMS verification code sent successfully");
				} else {
					this.verificationError = "Failed to send SMS verification code. Please try again.";
				}
			} catch (error) {
				console.error("Error setting up SMS:", error);
				this.verificationError = error.message || "Failed to send SMS. Please try again.";
			} finally {
				this.sendingSms = false;
			}
		},
		async verifyCode() {
			this.verifying = true;
			this.verificationError = null;
			try {
				// Get current user's Cognito access token
				const Auth = await import('@aws-amplify/auth').then(m => m.Auth);
				const session = await Auth.currentSession();
				const accessToken = session.getAccessToken().getJwtToken();
				
				const result = await orgService.mutation("verifyMfaSetup", {
					code: this.verificationCode,
					session: this.session,
					mfaMethod: this.mfaMethod,
					accessToken: accessToken
				});
				
				if (result && result.success) {
					this.setupComplete = true;
					this.step = 4;
					this.$emit('mfa-setup-complete', result.user);
				} else {
					this.verificationError = "Invalid verification code. Please try again.";
				}
			} catch (error) {
				console.error("Error verifying MFA:", error);
				this.verificationError = error.message || "Failed to verify code. Please try again.";
			} finally {
				this.verifying = false;
			}
		},
		copyToClipboard(text) {
			navigator.clipboard.writeText(text).then(() => {
				this.$emit('snackbar', 'Secret code copied to clipboard');
			});
		},
		skipSetup() {
			this.$emit('skip-setup');
		},
		closeDialog() {
			this.$emit('close');
		}
	}
};
</script>

<style scoped>
.qr-code {
	max-width: 300px;
	margin: 20px auto;
	display: block;
}
</style>

